import { useEffect, useMemo } from 'react';
import { useFetcher } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { CarouselSection } from '@rocco/ui/layout/components/CarouselSection';
import { ArticleTile } from '@rocco/ui/tile-article/components/ArticleTile';
import { mapArchizeenTileToEditorial } from '../util/map-archizeen-tile-to-editorial';
import {
    HREF_RECENTLY_VIEWED_ARTICLES,
    type loader,
} from '~/routes/remix-api.articles.get-recently-viewed-articles';
import { fetcherIsInit } from '~/utils/fetcherHelper';

interface EditorialRecentlyViewedProps {
    className?: string;
}

/**
 * The reader's recently viewed articles, closing both editorial templates.
 *
 * Composed exactly as the products, projects and professionals browse pages
 * close their grids: a rule, then CarouselSection carrying the heading and its
 * controls on one row above a drag-free strip. All four directories then page
 * and drag identically.
 *
 * Editorial supplies the parts that are its own — the bracketed Gestura title
 * the sections above it use, and the same article tile as the grid — through
 * the component's own title and children rather than a second heading, which
 * would leave CarouselSection's control row standing empty beneath it.
 *
 * Client-fetched, as the legacy section is — recently viewed is per-reader, so
 * it must not be baked into a cacheable document.
 */
export const EditorialRecentlyViewed = ({
    className,
}: EditorialRecentlyViewedProps) => {
    const fetcher = useFetcher<typeof loader>();
    const recentlyViewed = fetcher.data?.recentlyViewedArticles;

    useEffect(() => {
        if (fetcherIsInit(fetcher)) {
            fetcher.load(HREF_RECENTLY_VIEWED_ARTICLES);
        }
    }, [fetcher]);

    const tiles = useMemo(
        () =>
            (recentlyViewed ?? []).map((article) =>
                mapArchizeenTileToEditorial(article, {
                    variant: 'editorialLarge',
                }),
            ),
        [recentlyViewed],
    );

    if (tiles.length === 0) return null;

    return (
        <section
            aria-label="Recently viewed articles"
            className={cn('flex flex-col', className)}
        >
            {/* The section rule, on the carousel's own gutters so the two line
                up. Same hairline as EditorialSectionHeader draws. */}
            <div className="px-6 md:px-12">
                <hr className="m-0 h-0 border-t border-black/50" />
            </div>
            <CarouselSection
                title="(Recently Viewed)"
                titleClassName="m-0 font-gestura text-[2.0625rem] leading-[1.25] font-normal tracking-[-0.0516rem] text-[#181818]"
                size="recentlyViewed"
                dragFree
            >
                {tiles.map((tile) => (
                    <ArticleTile
                        key={tile.id}
                        model={tile}
                        aspectRatio="featured"
                        rounded={false}
                    />
                ))}
            </CarouselSection>
        </section>
    );
};
